// src/components/team/DropPlayerModal.tsx
import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Player } from '../../types';

interface DropPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  player: Player | null;
  isStarter?: boolean;
}

export default function DropPlayerModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  player,
  isStarter = false 
}: DropPlayerModalProps) {
  if (!isOpen || !player) return null;

  const getPositionColor = (position: string) => {
    return position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-danger-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-danger-600" />
            </div>
            <h2 className="text-lg font-semibold text-secondary-900">Drop Player</h2>
          </div>
          <button
            onClick={onClose}
            className="icon-btn text-secondary-400 hover:text-secondary-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Player Info */}
          <div className="flex items-center space-x-4 p-4 bg-secondary-50 rounded-lg mb-6">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary-600">
                {player.jersey_number ?? player.jerseyNumber}
              </span>
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="font-medium text-secondary-900">{player.name}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPositionColor(player.position)}`}>
                  {player.position}
                </span>
                <span className="text-sm text-secondary-600">
                  {(player.nfl_team || player.nflTeam)?.abbreviation}
                </span>
              </div>
              <div className="text-sm text-secondary-500">
                {player.experience ?? player.experienceYears} years experience
                {isStarter && (
                  <span className="ml-2 px-2 py-0.5 bg-warning-100 text-warning-800 rounded text-xs font-medium">
                    Currently Starting
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="space-y-3 text-sm text-secondary-700">
            <p>
              Are you sure you want to drop <strong>{player.name}</strong> from your roster?
            </p>
            
            {isStarter && (
              <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="w-4 h-4 text-warning-600 mt-0.5 flex-shrink-0" />
                  <p className="text-warning-800">
                    <strong>Warning:</strong> This player is currently in your starting lineup. 
                    Dropping them will leave an empty starter slot.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <p className="font-medium">What happens next:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Player will be removed from your roster immediately</li>
                <li>They'll become available for other teams to add</li>
                <li>You can't undo this action</li>
                {isStarter && (
                  <li className="text-warning-700">Your starting lineup will have an empty slot</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 bg-secondary-50">
          <button
            onClick={onClose}
            className="btn-outline-secondary"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="btn-danger"
          >
            Drop Player
          </button>
        </div>
      </div>
    </div>
  );
}